// @ts-nocheck
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import validatePhone from "@insite/client-framework/Common/Utilities/validatePhone";
import siteMessage from "@insite/client-framework/SiteMessage";
import resetReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ResetReCaptcha";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import loadCurrentCountries from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Select from "@insite/mobius/Select";
import TextArea from "@insite/mobius/TextArea";
import TextField from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FormActions from "@store/Library/Components/Toolkit/FormActions";
import { InfosBoxProps } from "@store/Library/Components/Toolkit/InfosBox";
import LoadingOverlay, { LoadingOverlayProps } from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import { formGridProps } from "@store/Themes/common/props/form";
import InfosBox from "@store/Library/Components/Toolkit/InfosBox";
import ReCaptcha from "@store/Overrides/Components/ReCaptcha";
import { useCurrentCountries } from "@toolkit/Framework/Store/Data/Countries/CountriesHooks";
import React, { ChangeEventHandler, FC, useEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { fetch } from "@insite/client-framework/ServerSideRendering";

const recaptchaLocation = "ContactUs";

const enum fields {
    eloquaFormName = "elqFormName",
    eloquaSiteId = "elqSiteId",
    actionURL = "actionURL",
    successModalTitle = "successModalTitle",
    successModalMessage = "successModalMessage",
    failureModalTitle = "failureModalTitle",
    failureModalMessage = "failureModalMessage",
    primaryReason = "primaryReason",
    describeYourself = "describeYourself",
}

export interface EloquaContactFormProps extends WidgetProps {
    fields: {
        [fields.eloquaFormName]: string;
        [fields.eloquaSiteId]: string;
        [fields.actionURL]: string;
        [fields.successModalTitle]: string;
        [fields.successModalMessage]: string;
        [fields.failureModalTitle]: string;
        [fields.failureModalMessage]: string;
        [fields.primaryReason]: PrimaryReasonFields[];
        [fields.describeYourself]: DescribeYourselfFields[];
    };
}

const enum primaryReasonFields {
    value = "value",
    label = "label",
}

interface PrimaryReasonFields {
    fields: {
        [primaryReasonFields.value]: string;
        [primaryReasonFields.label]: string;
    };
}

const enum describeYourselfFields {
    value = "value",
    label = "label",
}

interface DescribeYourselfFields {
    fields: {
        [describeYourselfFields.value]: string;
        [describeYourselfFields.label]: string;
    };
}

export interface EloquaContactFormStyles {
    errorMessage?: InfosBoxProps;
    gridContainer?: GridContainerProps;
    gridItem?: GridItemProps;
    gridItemFull?: GridItemProps;
    gridItemSmall?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    successMessage?: InfosBoxProps;
    wrapper?: InjectableCss;
    loadingOverlay?: LoadingOverlayProps;
    modal?: ModalPresentationProps;
}

const eloquaContactFormStyles: EloquaContactFormStyles = {
    successMessage: { type: "success" },
    errorMessage: { type: "error" },
    gridContainer: formGridProps.gridContainer,
    gridItem: { width: [12, 6, 6, 6, 6] },
    gridItemFull: { width: 12 },
    gridItemSmall: { width: [12, 4, 4, 4, 4] },
    submitButton: { variant: "primary" },
    modal: { sizeVariant: "large" },
};

const styles = eloquaContactFormStyles;

const EloquaContactForm: FC<EloquaContactFormProps> = ({ fields }) => {
    const dispatch = useAppDispatch();
    const formRef = useRef<HTMLFormElement>(null);
    const showErrorsRef = useRef(false);
    const [isLoading, setIsLoading] = useState(false);
    const recaptchaRef = useRef<ReCAPTCHA | null>(null);
    const countries = useCurrentCountries();
    const usStates = countries?.find(x => x.abbreviation === "US")?.states || [];
    const states = [
        ...usStates.map(x => ({ label: x.name, value: x.abbreviation })),
        // Canada provinces are hardcoded because Canada is not a transactional country assigned to the website
        { label: translate("Alberta"), value: "AB" },
        { label: translate("Manitoba"), value: "MB" },
        { label: translate("British Columbia"), value: "BC" },
        { label: translate("New Brunswick"), value: "NB" },
        { label: translate("Newfoundland"), value: "NL" },
        { label: translate("Nova Scotia"), value: "NS" },
        { label: translate("Northwest Territories"), value: "NT" },
        { label: translate("Nunavut"), value: "NU" },
        { label: translate("Ontario"), value: "ON" },
        { label: translate("Prince Edward Island"), value: "PE" },
        { label: translate("Quebec"), value: "QC" },
        { label: translate("Saskatchewan"), value: "SK" },
        { label: translate("Yukon Territory"), value: "YT" },
    ];

    const [firstName, setFirstName] = useState("");
    const [firstNameError, setFirstNameError] = useState("");
    const [lastName, setLastName] = useState("");
    const [lastNameError, setLastNameError] = useState("");
    const [emailAddress, setEmailAddress] = useState("");
    const [emailAddressError, setEmailAddressError] = useState("");
    const [company, setCompany] = useState("");
    const [companyError, setCompanyError] = useState("");
    const [businessPhone, setBusinessPhone] = useState("");
    const [businessPhoneError, setBusinessPhoneError] = useState("");
    const [zipCode, setZipCode] = useState("");
    const [zipCodeError, setZipCodeError] = useState("");
    const [city, setCity] = useState("");
    const [leadType, setLeadType] = useState("");
    const [leadTypeError, setLeadTypeError] = useState("");
    const [primaryReason, setPrimaryReason] = useState("");
    const [primaryReasonError, setPrimaryReasonError] = useState("");
    const [comments, setComments] = useState("");
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [state, setState] = useState("");
    const [stateError, setStateError] = useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);

    const clearFormFields = () => {
        setFirstName("");
        setLastName("");
        setEmailAddress("");
        setCompany("");
        setBusinessPhone("");
        setZipCode("");
        setCity("");
        setLeadType("");
        setPrimaryReason("");
        setComments("");
        setState("");
        setFirstNameError("");
        setLastNameError("");
        setCompanyError("");
        setZipCodeError("");
        setLeadTypeError("");
        setPrimaryReasonError("");
        setStateError("");
        setEmailAddressError("");
        resetReCaptcha({ location: recaptchaLocation });
    };

    useEffect(() => {
        if (!countries) {
            dispatch(loadCurrentCountries());
        }
    }, [countries, dispatch]);

    const firstNameLabel = translate("First Name");
    const validateFirstName = (value: string) => {
        const msg = !value.trim() ? siteMessage("Field_Required", firstNameLabel).toString() : "";
        if (showErrorsRef.current) {
            setFirstNameError(msg);
        }
        return !msg;
    };

    const lastNameLabel = translate("Last Name");
    const validateLastName = (value: string) => {
        const msg = !value.trim() ? siteMessage("Field_Required", lastNameLabel).toString() : "";
        if (showErrorsRef.current) {
            setLastNameError(msg);
        }
        return !msg;
    };

    const emailLabel = translate("Email Address");
    const validateEmailAddress = (value: string) => {
        const msg = !value
            ? siteMessage("CreateNewAccountInfo_EmailAddress_Required").toString()
            : validateEmail(value)
              ? ""
              : siteMessage("CreateNewAccountInfo_EmailAddress_ValidEmail").toString();
        if (showErrorsRef.current) {
            setEmailAddressError(msg);
        }
        return !msg;
    };

    const companyLabel = translate("Company");
    const validateCompany = (value: string) => {
        const msg = !value.trim() ? siteMessage("Field_Required", companyLabel).toString() : "";
        if (showErrorsRef.current) {
            setCompanyError(msg);
        }
        return !msg;
    };

    const businessPhoneLabel = translate("Business Phone");
    const phoneInvalidMessage = siteMessage("Field_Invalid", businessPhoneLabel).toString();
    const validatePhoneNumber = (value: string) => {
        const msg = value.trim() && !validatePhone(value) ? phoneInvalidMessage : "";
        setBusinessPhoneError(msg);
        return !msg;
    };

    const zipCodeLabel = translate("Zip Code");
    const validateZipCode = (value: string) => {
        const msg = !value.trim() ? siteMessage("Field_Required", zipCodeLabel).toString() : "";
        if (showErrorsRef.current) {
            setZipCodeError(msg);
        }
        return !msg;
    };

    const stateLabel = translate("State or Province");

    const cityLabel = translate("City");

    const LeadTypeLabel = translate("Please Describe Yourself");
    const validateLeadType = (value: string) => {
        const msg = !value.trim() ? siteMessage("Field_Required", LeadTypeLabel).toString() : "";
        if (showErrorsRef.current) {
            setLeadTypeError(msg);
        }
        return !msg;
    };

    const primaryReasonLabel = translate("What is the Primary Reason for Contacting Us?");
    const validatePrimaryReason = (value: string) => {
        const msg = !value.trim() ? translate("Primary Reason for Contacting Us is required") : "";
        if (showErrorsRef.current) {
            setPrimaryReasonError(msg);
        }
        return !msg;
    };

    const comments1Label = translate("Please Add Additional Comments / Questions Below");

    const onChangeState: ChangeEventHandler<HTMLSelectElement> = event => {
        const value = event.currentTarget.value;
        setState(value);
    };

    const submitHandler = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!isSubmitted) {
            setIsSubmitted(true);
        }

        showErrorsRef.current = true;

        const isFirstNameValid = validateFirstName(firstName);
        const isLastNameValid = validateLastName(lastName);
        const isEmailAddressValid = validateEmailAddress(emailAddress);
        const isCompanyValid = validateCompany(company);
        const isZipCodeValid = validateZipCode(zipCode);
        const isLeadTypeValid = validateLeadType(leadType);
        const isPrimaryReasonValid = validatePrimaryReason(primaryReason);
        const isPhoneValid = validatePhoneNumber(businessPhone);

        let isReCaptchaValid = false;
        await dispatch(
            validateReCaptcha({
                location: recaptchaLocation,
                recaptchaRef,
                onSuccess: isValid => {
                    isReCaptchaValid = isValid;
                },
            }),
        );

        if (
            !isFirstNameValid ||
            !isLastNameValid ||
            !isEmailAddressValid ||
            !isCompanyValid ||
            !isZipCodeValid ||
            !isLeadTypeValid ||
            !isPrimaryReasonValid ||
            !isPhoneValid ||
            !isReCaptchaValid
        ) {
            return;
        }

        setIsLoading(true);

        const formData = new FormData(formRef.current!);

        try {
            await fetch(fields.actionURL, {
                method: "POST",
                body: formData,
                mode: "no-cors",
            });

            setIsLoading(false);
            setIsModalOpen(true);
            clearFormFields();
        } catch (error) {
            setIsLoading(false);
            setIsErrorModalOpen(true);
        }
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <LoadingOverlay {...styles.loadingOverlay} loading={isLoading}>
                <form
                    method="post"
                    name={fields.elqFormName}
                    action={fields.actionURL}
                    onSubmit={submitHandler}
                    ref={formRef}
                    noValidate
                >
                    <input value={fields.elqFormName} type="hidden" name="elqFormName" />
                    <input value={fields.elqSiteId} type="hidden" name="elqSiteId" />

                    <GridContainer {...styles.gridContainer}>
                        <GridItem {...styles.gridItem}>
                            <TextField
                                label={firstNameLabel}
                                name="firstName"
                                type="text"
                                required
                                value={firstName}
                                onChange={e => {
                                    setFirstName(e.target.value);
                                    validateFirstName(e.target.value);
                                }}
                                error={firstNameError}
                                autoComplete="given-name"
                            />
                        </GridItem>
                        <GridItem {...styles.gridItem}>
                            <TextField
                                label={lastNameLabel}
                                name="lastName"
                                type="text"
                                required
                                value={lastName}
                                onChange={e => {
                                    setLastName(e.target.value);
                                    validateLastName(e.target.value);
                                }}
                                error={lastNameError}
                                autoComplete="family-name"
                            />
                        </GridItem>
                        <GridItem {...styles.gridItemFull}>
                            <TextField
                                label={emailLabel}
                                name="emailAddress"
                                type="email"
                                required
                                value={emailAddress}
                                onChange={e => {
                                    setEmailAddress(e.currentTarget.value);
                                    validateEmailAddress(e.currentTarget.value);
                                }}
                                error={emailAddressError}
                                autoComplete="email"
                            />
                        </GridItem>
                        <GridItem {...styles.gridItem}>
                            <TextField
                                label={companyLabel}
                                name="customFieldOne2"
                                type="text"
                                required
                                value={company}
                                onChange={e => {
                                    setCompany(e.target.value);
                                    validateCompany(e.target.value);
                                }}
                                error={companyError}
                                autoComplete="organization"
                            />
                        </GridItem>
                        <GridItem {...styles.gridItem}>
                            <TextField
                                label={businessPhoneLabel}
                                name="customFieldTwo3"
                                type="tel"
                                inputMode="tel"
                                value={businessPhone}
                                onChange={e => {
                                    setBusinessPhone(e.target.value);
                                    validatePhoneNumber(e.currentTarget.value);
                                }}
                                autoComplete="tel"
                                error={businessPhoneError}
                            />
                        </GridItem>
                        <GridItem {...styles.gridItemSmall}>
                            <TextField
                                label={zipCodeLabel}
                                name="customFieldOne"
                                type="text"
                                inputMode="numeric"
                                required
                                value={zipCode}
                                onChange={e => {
                                    setZipCode(e.target.value);
                                    validateZipCode(e.target.value);
                                }}
                                error={zipCodeError}
                                autoComplete="postal-code"
                            />
                        </GridItem>
                        <GridItem {...styles.gridItemSmall}>
                            <Select
                                label={stateLabel}
                                name="stateProv"
                                onChange={onChangeState}
                                value={state}
                                autoComplete="address-level1"
                            >
                                <option value="">{translate("Please select...")}</option>
                                {states?.map(state => {
                                    return (
                                        <option key={state.value} value={state.value}>
                                            {state.label}
                                        </option>
                                    );
                                })}
                            </Select>
                        </GridItem>
                        <GridItem {...styles.gridItemSmall}>
                            <TextField
                                label={cityLabel}
                                name="customFieldTwo2"
                                type="text"
                                value={city}
                                onChange={e => setCity(e.target.value)}
                                autoComplete="address-level2"
                            />
                        </GridItem>
                        <GridItem {...styles.gridItemFull}>
                            <Select
                                label={LeadTypeLabel}
                                name="leadType1"
                                required
                                value={leadType}
                                onChange={e => {
                                    setLeadType(e.target.value);
                                    validateLeadType(e.target.value);
                                }}
                                error={leadTypeError}
                            >
                                <option value="">{translate("Please select...")}</option>
                                {fields.describeYourself.map(({ fields: { label, value } }) => (
                                    <option key={value} value={value}>
                                        {label || value}
                                    </option>
                                ))}
                            </Select>
                        </GridItem>
                        <GridItem {...styles.gridItemFull}>
                            <Select
                                label={primaryReasonLabel}
                                name="dropdownMenu"
                                required
                                value={primaryReason}
                                onChange={e => {
                                    setPrimaryReason(e.target.value);
                                    validatePrimaryReason(e.target.value);
                                }}
                                error={primaryReasonError}
                            >
                                <option value="">{translate("Please select...")}</option>
                                {fields.primaryReason.map(({ fields: { label, value } }) => (
                                    <option key={value} value={value}>
                                        {label || value}
                                    </option>
                                ))}
                            </Select>
                        </GridItem>
                        <GridItem {...styles.gridItemFull}>
                            <TextArea
                                label={comments1Label}
                                name="comments1"
                                value={comments}
                                onChange={e => setComments(e.target.value)}
                            />
                        </GridItem>
                        <GridItem {...styles.gridItemFull}>
                            <ReCaptcha recaptchaRef={recaptchaRef} location={recaptchaLocation} />
                        </GridItem>
                        <GridItem {...styles.gridItemFull}>
                            <FormActions>
                                <Button {...styles.submitButton} type="submit">
                                    {translate("Submit")}
                                </Button>
                            </FormActions>
                        </GridItem>
                    </GridContainer>
                </form>
                <Modal
                    {...styles.modal}
                    headline={fields.successModalTitle}
                    isOpen={isModalOpen}
                    handleClose={() => setIsModalOpen(false)}
                >
                    <InfosBox {...styles.successMessage}>{fields.successModalMessage}</InfosBox>
                    <FormActions>
                        <Button {...styles.submitButton} onClick={() => setIsModalOpen(false)}>
                            {translate("Close")}
                        </Button>
                    </FormActions>
                </Modal>
                <Modal
                    {...styles.modal}
                    headline={fields.failureModalTitle}
                    isOpen={isErrorModalOpen}
                    handleClose={() => setIsErrorModalOpen(false)}
                >
                    <InfosBox {...styles.errorMessage}>{fields.failureModalMessage}</InfosBox>
                    <FormActions>
                        <Button {...styles.submitButton} onClick={() => setIsErrorModalOpen(false)}>
                            {translate("Close")}
                        </Button>
                    </FormActions>
                </Modal>
            </LoadingOverlay>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule<Toolkit.FieldDefinition> = {
    component: EloquaContactForm,
    definition: {
        group: "Contact Us",
        displayName: "Eloqua Contact Form",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.eloquaFormName,
                displayName: "Eloqua Form Name",
                fieldType: "Translatable",
                editorTemplate: "TextField",
                defaultValue: "EmserWebsiteContactUsFormTest",
                isRequired: true,
            },
            {
                name: fields.eloquaSiteId,
                displayName: "Eloqua Site ID",
                fieldType: "Translatable",
                editorTemplate: "TextField",
                defaultValue: "2023555113",
                isRequired: true,
            },
            {
                name: fields.actionURL,
                displayName: "action URL",
                fieldType: "Translatable",
                editorTemplate: "TextField",
                defaultValue: "https://s2023555113.t.eloqua.com/e/f2",
                isRequired: true,
            },
            {
                name: fields.successModalTitle,
                displayName: "Success Title",
                fieldType: "Translatable",
                editorTemplate: "TextField",
                defaultValue: "Submission Success",
                isRequired: true,
            },
            {
                name: fields.successModalMessage,
                displayName: "Success Message",
                fieldType: "Translatable",
                editorTemplate: "TextField",
                defaultValue: "Thank you! Your form has been submitted successfully. We will get back to you shortly.",
                isRequired: true,
            },
            {
                name: fields.failureModalTitle,
                displayName: "Error Title",
                fieldType: "Translatable",
                editorTemplate: "TextField",
                defaultValue: "Submission Error",
                isRequired: true,
            },
            {
                name: fields.failureModalMessage,
                displayName: "Error Message",
                fieldType: "Translatable",
                editorTemplate: "TextField",
                defaultValue: "We could not submit your form at this time, please try again later or call us.",
                isRequired: true,
            },
            {
                name: fields.describeYourself,
                displayName: "Describe Yourself",
                editorTemplate: "ListField",
                defaultValue: [
                    {
                        fields: {
                            value: "Architect/Designer",
                            label: "Architect/Designer",
                        },
                    },
                    {
                        fields: {
                            value: "Commercial Developer",
                            label: "Commercial Developer",
                        },
                    },
                    {
                        fields: {
                            value: "Contractor",
                            label: "Contractor",
                        },
                    },
                    {
                        fields: {
                            value: "Fabricator",
                            label: "Fabricator",
                        },
                    },
                    {
                        fields: {
                            value: "Home Owner",
                            label: "Home Owner",
                        },
                    },
                    {
                        fields: {
                            value: "Residential Builder",
                            label: "Residential Builder",
                        },
                    },
                    {
                        fields: {
                            value: "Retailer",
                            label: "Retailer",
                        },
                    },
                    {
                        fields: {
                            value: "Other",
                            label: "Other",
                        },
                    },
                ],
                getDisplay: item => item.fields.label || item.fields.value,
                fieldType: "Contextual",
                fieldDefinitions: [
                    {
                        name: describeYourselfFields.value,
                        defaultValue: "",
                        displayName: "value",
                        editorTemplate: "TextField",
                        isRequired: true,
                    },
                    {
                        name: describeYourselfFields.label,
                        defaultValue: "",
                        displayName: "label",
                        editorTemplate: "TextField",
                    },
                ],
            },
            {
                name: fields.primaryReason,
                displayName: "Contact Reason",
                editorTemplate: "ListField",
                defaultValue: [
                    {
                        fields: {
                            value: "Reseller",
                            label: "Become an Authorized Reseller",
                        },
                    },
                    {
                        fields: {
                            value: "Samples",
                            label: "Samples",
                        },
                    },
                    {
                        fields: {
                            value: "Product",
                            label: "Product Inquiry",
                        },
                    },
                    {
                        fields: {
                            value: "Technical",
                            label: "Technical / Usage & Care / Installation",
                        },
                    },
                    {
                        fields: {
                            value: "Customer Service",
                            label: "Customer Service",
                        },
                    },
                    {
                        fields: {
                            value: "Contact Me",
                            label: "Have My Rep Contact Me",
                        },
                    },
                    {
                        fields: {
                            value: "Other",
                            label: "Other",
                        },
                    },
                ],
                getDisplay: item => item.fields.label || item.fields.value,
                fieldType: "Contextual",
                fieldDefinitions: [
                    {
                        name: primaryReasonFields.value,
                        defaultValue: "",
                        displayName: "value",
                        editorTemplate: "TextField",
                        isRequired: true,
                    },
                    {
                        name: primaryReasonFields.label,
                        defaultValue: "",
                        displayName: "label",
                        editorTemplate: "TextField",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
